import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { EmotionPage } from "@/components/feelings/EmotionPage";
import { CORE_FEELINGS, getFeeling } from "@/data/feelings-pathway";

export const Route = createFileRoute("/how-are-you-feeling/$feeling")({
  // The feelings content is fetched when this page opens, not on every visit.
  loader: async ({ params }) => {
    if (params.feeling === "unknown") {
      return { key: params.feeling, label: "I don't know what I'm feeling" };
    }
    const { getFeeling: lookup } = await import("@/data/feelings-pathway");
    const feeling = lookup(params.feeling);
    if (!feeling) throw notFound();
    return { key: params.feeling, label: feeling.label };
  },

  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Unavailable — Recovery Trauma™" }, { name: "robots", content: "noindex" }] };
    }
    const title = `${loaderData.label} — How Are You Feeling? | Recovery Trauma™`;
    const description =
      loaderData.key === "unknown"
        ? "Can't name what you're feeling? A gentle, body-first way to begin, with reflection, needs and a short trauma-informed exercise."
        : `Understand ${loaderData.label.toLowerCase()}, what can sit underneath it, what you might need, and a short trauma-informed exercise from Recovery Trauma™.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: FeelingDetailPage,
  notFoundComponent: FeelingNotFound,
});

function FeelingDetailPage() {
  const { key, label } = Route.useLoaderData();
  const membership = useMembership();
  const hasAccess = membership.isMember || membership.isAdmin;
  const feeling = key === "unknown" ? null : (getFeeling(key) ?? null);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <Link
        to="/how-are-you-feeling"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Back to the emotion wheel
      </Link>
      <h1 className="mt-3 font-display text-2xl text-foreground sm:text-3xl">{label}</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Understand it, reflect on it, notice what you might need, try a short exercise, and keep
        anything you write — privately, only if you want to.
      </p>
      <div className="mt-6">
        <EmotionPage feeling={feeling} hasMemberAccess={hasAccess} />
      </div>

      <nav aria-label="Other feelings" className="mt-10 border-t border-gold/15 pt-5">
        <p className="text-sm font-medium text-foreground">Another feeling</p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {CORE_FEELINGS.filter((f) => f.key !== key).map((f) => (
            <li key={f.key}>
              <Link
                to="/how-are-you-feeling/$feeling"
                params={{ feeling: f.key }}
                className="inline-flex rounded-full border border-gold/30 px-3 py-1.5 text-xs font-medium text-foreground/80 hover:border-gold"
              >
                {f.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </main>
  );
}

function FeelingNotFound() {
  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-16 text-center">
      <h1 className="font-display text-2xl text-foreground">We couldn't find that feeling</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        You can choose from the emotion wheel instead — there's no wrong answer.
      </p>
      <Link
        to="/how-are-you-feeling"
        className="mt-5 inline-flex min-h-[48px] items-center rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-[var(--cream,#FBF7EF)]"
      >
        Open the emotion wheel
      </Link>
    </main>
  );
}
